import * as XLSX from 'xlsx';

interface ScheduleEvent {
  id: string;
  title: string;
  startTime: string; // Format: HH:MM
  endTime: string;   // Format: HH:MM
  location: string;
  date: string;      // Format: YYYY-MM-DD
  description?: string;
  type?: string;     // For color coding
}

interface ExcelParseResult {
  events: ScheduleEvent[];
  errors: string[];
  warnings: string[];
}

/**
 * Helper function to get an array of dates between start and end (inclusive)
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Array of date strings in YYYY-MM-DD format
 */
function getDatesInRange(startDate: Date, endDate: Date): string[] {
  const dates: string[] = [];
  const currentDate = new Date(startDate);
  
  // Set times to noon to avoid timezone issues
  currentDate.setHours(12, 0, 0, 0);
  const endDateTime = new Date(endDate);
  endDateTime.setHours(12, 0, 0, 0);
  
  // Loop through dates and add them to the array
  while (currentDate <= endDateTime) {
    const dateString = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
    dates.push(dateString);
    
    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
}

/**
 * Validates and parses an Excel file for schedule events
 * @param file - The Excel file as an ArrayBuffer
 * @returns Object containing parsed events and any validation messages
 */
export const parseExcelSchedule = (file: ArrayBuffer): ExcelParseResult => {
  const result: ExcelParseResult = {
    events: [],
    errors: [],
    warnings: []
  };
  
  try {
    // Read the workbook with full options for best compatibility
    // Note the change from cellFormulas to cellFormula to match SheetJS types
    const workbook = XLSX.read(file, {
      cellStyles: true,
      cellFormula: true, // Corrected from cellFormulas to cellFormula
      cellDates: true,
      cellNF: true,
      sheetStubs: true
    });
    
    // Verify that we have at least one sheet
    if (workbook.SheetNames.length === 0) {
      result.errors.push('The Excel file does not contain any sheets.');
      return result;
    }
    
    // Use the first sheet by default - we could make this configurable
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Convert to JSON for easier processing
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    
    // Find the headers row (typically row 3 in our example file)
    let headersRow = -1;
    for (let i = 0; i < Math.min(10, jsonData.length); i++) {
      const row = jsonData[i] as any[];
      if (Array.isArray(row) && row.length >= 3) {
        const potentialHeaders = row.map(cell => String(cell).trim());
        
        // Check if this row contains our expected headers
        if (
          potentialHeaders.includes('Event') && 
          (
            potentialHeaders.includes('Date Start') || 
            potentialHeaders.includes('Time Start') ||
            potentialHeaders.includes('Date') ||
            potentialHeaders.includes('Start Date')
          )
        ) {
          headersRow = i;
          break;
        }
      }
    }
    
    if (headersRow === -1) {
      result.errors.push('Could not find required headers in the Excel file. Please ensure it has columns for Event, Date/Date Start, and Time Start.');
      return result;
    }
    
    // Map the headers to their column indices
    const headers = (jsonData[headersRow] as any[]).map(h => String(h).trim());
    
    // Create a mapping for required and optional fields
    // We support multiple possible header names for flexibility
    const headerMappings: Record<string, string[]> = {
      event: ['Event', 'Title', 'Event Name', 'Event Title'],
      dateStart: ['Date Start', 'Start Date', 'Date', 'Day'],
      dateEnd: ['Date End', 'End Date'],
      timeStart: ['Time Start', 'Start Time', 'Start'],
      timeEnd: ['Time End', 'End Time', 'End'],
      location: ['Room', 'Location', 'Venue', 'Place']
    };
    
    // Map header names to column indices
    const columnIndices: Record<string, number> = {};
    
    // For each field, find the first matching header
    for (const [field, possibleNames] of Object.entries(headerMappings)) {
      const index = headers.findIndex(header => possibleNames.includes(header));
      if (index !== -1) {
        columnIndices[field] = index;
      }
    }
    
    // Check if we have the minimum required columns
    if (!('event' in columnIndices)) {
      result.errors.push('Missing required "Event" column in the Excel file.');
      return result;
    }
    
    if (!('dateStart' in columnIndices) && !('timeStart' in columnIndices)) {
      result.errors.push('Missing required date or time columns in the Excel file.');
      return result;
    }
    
    // Process data rows
    const events: ScheduleEvent[] = [];
    let skippedRows = 0;
    
    for (let i = headersRow + 1; i < jsonData.length; i++) {
      const row = jsonData[i] as any[];
      
      // Skip empty rows or rows without an event title
      if (!row || row.length === 0 || !row[columnIndices.event]) {
        continue;
      }
      
      // Extract event title
      const title = String(row[columnIndices.event]).trim();
      if (!title) {
        skippedRows++;
        continue;
      }
      
      // Initialize date and time variables
      let startDate: Date | null = null;
      let endDate: Date | null = null;
      let startTime = '';
      let endTime = '';
      
      // Process date information
      if ('dateStart' in columnIndices && row[columnIndices.dateStart]) {
        const dateValue = row[columnIndices.dateStart];
        
        // Handle Excel date values (could be Date object or serialized date)
        if (dateValue instanceof Date) {
          startDate = dateValue;
        } else if (typeof dateValue === 'number') {
          // Excel stores dates as serial numbers, so we need to convert
          startDate = new Date(XLSX.SSF.parse_date_code(dateValue));
        } else if (typeof dateValue === 'string') {
          // Try to parse string date
          try {
            startDate = new Date(dateValue);
            if (isNaN(startDate.getTime())) {
              // If invalid, see if it's in DD/MM/YYYY format
              const parts = dateValue.split(/[\/\-\.]/);
              if (parts.length === 3) {
                // Try different date formats (MM/DD/YYYY, DD/MM/YYYY)
                startDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // DD/MM/YYYY
                if (isNaN(startDate.getTime())) {
                  startDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`); // MM/DD/YYYY
                }
              }
            }
          } catch (e) {
            result.warnings.push(`Row ${i+1}: Could not parse date "${dateValue}"`);
            skippedRows++;
            continue;
          }
        }
      }
      
      // Process end date if available
      if ('dateEnd' in columnIndices && row[columnIndices.dateEnd]) {
        const dateValue = row[columnIndices.dateEnd];
        
        if (dateValue instanceof Date) {
          endDate = dateValue;
        } else if (typeof dateValue === 'number') {
          endDate = new Date(XLSX.SSF.parse_date_code(dateValue));
        } else if (typeof dateValue === 'string') {
          try {
            endDate = new Date(dateValue);
            if (isNaN(endDate.getTime())) {
              const parts = dateValue.split(/[\/\-\.]/);
              if (parts.length === 3) {
                endDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                if (isNaN(endDate.getTime())) {
                  endDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
                }
              }
            }
          } catch (e) {
            // If end date is invalid, just use start date
            endDate = startDate;
          }
        }
      } else {
        // If no end date provided, use start date
        endDate = startDate;
      }
      
      // Process time information
      if ('timeStart' in columnIndices && row[columnIndices.timeStart]) {
        const timeValue = row[columnIndices.timeStart];
        
        if (timeValue instanceof Date) {
          startTime = `${timeValue.getHours().toString().padStart(2, '0')}:${timeValue.getMinutes().toString().padStart(2, '0')}`;
        } else if (typeof timeValue === 'string') {
          // Try to extract time from string
          const timeMatch = timeValue.match(/(\d{1,2}):(\d{2})/);
          if (timeMatch) {
            const hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            if (!isNaN(hours) && !isNaN(minutes)) {
              startTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
          } else {
            // Try AM/PM format
            const ampmMatch = timeValue.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
            if (ampmMatch) {
              let hours = parseInt(ampmMatch[1]);
              const minutes = ampmMatch[2] ? parseInt(ampmMatch[2]) : 0;
              const isPM = ampmMatch[3].toLowerCase() === 'pm';
              
              if (isPM && hours < 12) hours += 12;
              if (!isPM && hours === 12) hours = 0;
              
              startTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
          }
        } else if (typeof timeValue === 'number') {
          // Excel time values are fractions of a day
          const totalMinutes = Math.round(timeValue * 24 * 60);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          startTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
      } else if (startDate) {
        // Extract time from date if time field is not separate
        startTime = `${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`;
      }
      
      // Process end time
      if ('timeEnd' in columnIndices && row[columnIndices.timeEnd]) {
        const timeValue = row[columnIndices.timeEnd];
        
        if (timeValue instanceof Date) {
          endTime = `${timeValue.getHours().toString().padStart(2, '0')}:${timeValue.getMinutes().toString().padStart(2, '0')}`;
        } else if (typeof timeValue === 'string') {
          const timeMatch = timeValue.match(/(\d{1,2}):(\d{2})/);
          if (timeMatch) {
            const hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            if (!isNaN(hours) && !isNaN(minutes)) {
              endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
          } else {
            const ampmMatch = timeValue.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
            if (ampmMatch) {
              let hours = parseInt(ampmMatch[1]);
              const minutes = ampmMatch[2] ? parseInt(ampmMatch[2]) : 0;
              const isPM = ampmMatch[3].toLowerCase() === 'pm';
              
              if (isPM && hours < 12) hours += 12;
              if (!isPM && hours === 12) hours = 0;
              
              endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            }
          }
        } else if (typeof timeValue === 'number') {
          const totalMinutes = Math.round(timeValue * 24 * 60);
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
      } else if (endDate) {
        endTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
      } else {
        // If no end time, default to 1 hour after start time
        if (startTime) {
          const [hours, minutes] = startTime.split(':').map(Number);
          const endHour = (hours + 1) % 24;
          endTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
      }
      
      // Get location/room if available
      let location = '';
      if ('location' in columnIndices && row[columnIndices.location]) {
        location = String(row[columnIndices.location]).trim();
      }
      
      // Skip if we don't have enough information
      if (!title || !startDate || !startTime || !endTime) {
        skippedRows++;
        continue;
      }
      
      // Determine event type based on title
      const type = determineEventType(title);
      
      // Check if this is a multi-day event
      if (endDate && startDate && endDate.getTime() !== startDate.getTime()) {
        // Get all dates in the range
        const dates = getDatesInRange(startDate, endDate);
        
        // Create an event for each date in the range
        dates.forEach(dateStr => {
          events.push({
            id: `imported-${i}-${Date.now()}-${dateStr}`,
            title,
            date: dateStr,
            startTime,
            endTime,
            location,
            description: '',
            type
          });
        });
      } else {
        // Single day event
        const eventDate = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
        
        events.push({
          id: `imported-${i}-${Date.now()}`,
          title,
          date: eventDate,
          startTime,
          endTime,
          location,
          description: '',
          type
        });
      }
    }
    
    if (events.length === 0) {
      result.errors.push('No valid events found in the Excel file.');
      return result;
    }
    
    if (skippedRows > 0) {
      result.warnings.push(`Skipped ${skippedRows} rows due to missing or invalid data.`);
    }
    
    result.events = events;
    
  } catch (error) {
    console.error('Error parsing Excel file:', error);
    result.errors.push(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
  
  return result;
};

/**
 * Helper function to determine event type based on title for color coding
 */
const determineEventType = (title: string): string => {
  const lowerTitle = title.toLowerCase();
  
  if (lowerTitle.includes('break') || lowerTitle.includes('lunch') || lowerTitle.includes('coffee') || lowerTitle.includes('refreshment')) {
    return 'break';
  }
  
  if (lowerTitle.includes('workshop') || lowerTitle.includes('training') || lowerTitle.includes('seminar')) {
    return 'workshop';
  }
  
  if (lowerTitle.includes('ceremony') || lowerTitle.includes('opening') || lowerTitle.includes('closing') || lowerTitle.includes('award')) {
    return 'ceremony';
  }
  
  if (lowerTitle.includes('panel') || lowerTitle.includes('discussion') || lowerTitle.includes('debate') || lowerTitle.includes('forum')) {
    return 'panel';
  }
  
  return '';
};